import { useEffect, useRef, useState } from "react";

/**
 * Reveal — fades and lifts content as it enters viewport.
 * Uses IntersectionObserver + CSS for maximum reliability.
 */
export const Reveal = ({
  children,
  delay = 0,
  y = 28,
  className = "",
  amount = 0.2,
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // If already in viewport on mount, show immediately.
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: amount },
    );
    obs.observe(node);
    // Safety: also reveal after 2s in case observer fails.
    const t = setTimeout(() => setShown(true), 2000);
    return () => {
      obs.disconnect();
      clearTimeout(t);
    };
  }, [amount]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

export const RevealLine = ({ delay = 0 }) => (
  <span
    className="block h-px bg-current origin-left"
    style={{
      transform: "scaleX(1)",
      transition: `transform 1.2s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
    }}
  />
);
